import { computed, observable, isObservableArray } from 'mobx';
import get from 'lodash/get';
import { compareValuesArray } from '../utils/compareValues';
import { performanceCheck as checkPerformance } from '../utils/performanceCheck';
import { Config, InclusionClauses } from '../models/config';
import { ShareClass, ShareClassDocument } from '../models/entity';
import { BaseStore } from './BaseStore';
import { Stores } from '../models/Stores';

export class DocumentStore extends BaseStore {
    @observable config: Config;
    @observable shareClass: ShareClass;
    @observable globalDocuments: Array<ShareClassDocument>;

    constructor(
        stores: Stores,
        config: Config,
        shareClass: ShareClass,
        globalDocuments: Array<ShareClassDocument>
    ) {
        super(stores);
        this.config = config;
        this.shareClass = shareClass;
        this.globalDocuments = globalDocuments;
    }

    // Add global documents to shareclass documents
    @computed
    get shareClassDocuments(): Array<ShareClassDocument> {
        const { documents = [] } = this.shareClass;
        const allDocuments = documents.concat(this.globalDocuments);

        // Return unique documents
        return Array.from(new Set(allDocuments));
    }

    // Filter documents with documentSegmentationFilters
    @computed
    get filteredDocuments(): Array<ShareClassDocument> {
        const {
            inclusionClauses = [],
        } = this.config.documentSegmentationFilters;

        if (inclusionClauses.length > 0) {
            let includedDocuments = [];

            // Aggregate included documents
            for (const inclusionClause of inclusionClauses) {
                const { clauses, performanceCheck } = inclusionClause;

                const hasPerformance = checkPerformance({
                    dataStore: this.stores.data,
                    config: this.config,
                    shareClass: this.shareClass,
                });

                // Filter documents by each set of clauses
                includedDocuments = includedDocuments.concat(
                    this.shareClassDocuments.filter(d =>
                        this.documentsFilter(
                            d,
                            clauses,
                            performanceCheck,
                            hasPerformance
                        )
                    )
                );
            }

            // Return unique documents
            return Array.from(new Set(includedDocuments));
        }

        return this.shareClassDocuments;
    }

    documentsFilter = (
        document: ShareClassDocument,
        clauses: InclusionClauses,
        performanceCheck: boolean,
        hasPerformance: boolean
    ): boolean => {
        const clauseResults = [];

        // For each clause, record whether the document matches clause critera
        for (const clause of clauses) {
            const { property, metatype, operator, value } = clause as any; // as InclusionClauses;
            const clauseValuesArray = isObservableArray(value)
                ? value
                : [value];

            if (property) {
                const propertyValue = get(
                    this.shareClass,
                    `properties_pub.${property}.value`
                );
                const propertyValuesArray = isObservableArray(propertyValue)
                    ? propertyValue
                    : [propertyValue];
                const isMatch = compareValuesArray(
                    clauseValuesArray,
                    operator,
                    propertyValuesArray
                );
                clauseResults.push(isMatch);
            }

            if (metatype) {
                const metaValue = get(document, `meta.${metatype}.value`);
                const metaValuesArray = isObservableArray(metaValue)
                    ? metaValue
                    : [metaValue];
                const isMatch = compareValuesArray(
                    clauseValuesArray,
                    operator,
                    metaValuesArray
                );
                clauseResults.push(isMatch);
            }

            if (performanceCheck) {
                clauseResults.push(hasPerformance);
            }
        }

        // Return true only if every clause result is true
        return clauseResults.every(v => v === true);
    };
}



// WEBPACK FOOTER //
// ./src/stores/DocumentStore.ts